"use client";

// GitGlyph — THE git marks: one family on one grid, so every git thing the
// product shows (the repo, a branch, a merge, a pr, a commit, a push, a pull,
// the lanes, the first commit) and the memory that moves beside it (a decision,
// a note, netti's fold, a handoff, a task) reads as one quiet set.
//
// The grid (refined from the 45 drafts, 2026-09-23 — John picked #26 and #29):
//   · a 64×64 box. Two frames:
//       cell  — the brand cell (CELL.path scaled to 58 wide, centred) holds the
//               glyph: repo · branch · merge · pr · push · pull · decision ·
//               capture · task
//       free  — the glyph IS cells on a thread: commit · lanes · root · fold ·
//               handoff. Same envelope (x 4..60, y 10..55), so optical size holds.
//   · one weight: strokes 3 with round caps and joins, nodes r 3.4, every rim
//     2.6 (the big cell and the small ones alike).
//   · two forms of every glyph:
//       outline — rims drawn, strokes and nodes in ink
//       solid   — the cell frame filled with the glyph knocked out through a
//                 mask (strokes 3.6, nodes 4.2 so the cut survives at 14px); a
//                 free glyph's cells filled, each parted from what it overlaps by
//                 a thin gap
//   · time runs UP, as it does in the repo view (newest on top): a branch
//     leaves its trunk upward, a merge gathers two lines into one node above.
//
// `currentColor` everywhere, so a glyph rides the ink ladder (use --ink-3 at
// 14–16px, never louder than the words beside it). Decorative unless `title`.

import { useId } from "react";
import { CELL } from "../../lib/brand/cell";

export const GLYPH_STROKE = 3;
export const GLYPH_NODE = 3.4;
export const GLYPH_RIM = 2.6;
const KNOCK_STROKE = 3.6;
const KNOCK_NODE = 4.2;
const GAP = 2.2; // the parting between two filled cells (solid, free frame)
const FRAME_W = 58; // the brand cell, centred in the box

type Part =
  | { t: "path"; d: string }
  | { t: "dot"; x: number; y: number; r?: number }
  | { t: "ring"; x: number; y: number; r: number }
  | { t: "cell"; cx: number; cy: number; w: number };

export type GitGlyphKind =
  | "repo"
  | "branch"
  | "merge"
  | "commit"
  | "pr"
  | "push"
  | "pull"
  | "lanes"
  | "root"
  | "fold"
  | "decision"
  | "capture"
  | "task"
  | "handoff";

export interface GitGlyphDef {
  /** the word the key shows beside it (lowercase, plain) */
  word: string;
  /** which draft it grew from, by its number on the sheet */
  from: string;
  frame: "cell" | "free";
  parts: Part[];
  /** a cell-frame glyph whose thread leaves (or enters) the rim */
  escapes?: boolean;
}

const p = (d: string): Part => ({ t: "path", d });
const dot = (x: number, y: number, r?: number): Part => ({ t: "dot", x, y, r });
const ring = (x: number, y: number, r: number): Part => ({ t: "ring", x, y, r });
const cell = (cx: number, cy: number, w: number): Part => ({ t: "cell", cx, cy, w });

/** the family. Order is the key's order: git first, then the thinking beside it. */
export const GIT_GLYPHS: Record<GitGlyphKind, GitGlyphDef> = {
  repo: {
    word: "repo",
    from: "#26 · #29",
    frame: "cell",
    parts: [p("M24 24 L16 32 L24 40"), p("M40 24 L48 32 L40 40"), p("M35 21 L29 43")],
  },
  branch: {
    word: "branch",
    from: "#1 · #6",
    frame: "cell",
    parts: [p("M28 44 V21"), p("M28 37 C28 31 37 30 37 24"), dot(28, 44), dot(28, 21), dot(37, 24)],
  },
  merge: {
    word: "merge",
    from: "#3 · #8",
    frame: "cell",
    parts: [p("M24 44 C24 35 32 33 32 21"), p("M40 44 C40 35 32 33 32 21"), dot(24, 44), dot(40, 44), dot(32, 21)],
  },
  pr: {
    word: "pr",
    from: "#27",
    frame: "cell",
    parts: [p("M24 24 L17 32 L24 40"), p("M33 41 V23"), p("M33 35 C33 29 42 30 42 24"), dot(33, 41), dot(33, 23), dot(42, 24)],
  },
  commit: {
    word: "commit",
    from: "#17",
    frame: "free",
    parts: [p("M8 32 H56"), cell(32, 32, 28), dot(8, 32), dot(56, 32)],
  },
  push: {
    word: "push",
    from: "#36",
    frame: "cell",
    escapes: true,
    // from a node in the cell, out through the rim, to a node beyond it
    parts: [p("M31 36 C39 30 46 21 56 11"), dot(31, 36), dot(56, 11)],
  },
  pull: {
    word: "pull",
    from: "#37",
    frame: "cell",
    escapes: true,
    // push turned about: a thread from beyond the rim, landing on a node inside
    parts: [p("M33 36 C25 30 18 21 8 11"), dot(33, 36)],
  },
  lanes: {
    word: "lanes",
    from: "#18",
    frame: "free",
    parts: [p("M12 42 H52"), p("M18 42 C27 42 27 22 36 22 H52"), cell(12, 42, 15), cell(52, 42, 15), cell(52, 22, 15)],
  },
  root: {
    word: "first commit",
    from: "#11",
    frame: "free",
    parts: [p("M20 44 C27 33 31 26 43 18"), p("M31 29 C36 30 40 33 47 34"), cell(18, 46, 22), dot(43, 18), dot(47, 34)],
  },
  fold: {
    word: "fold",
    from: "#21",
    frame: "free",
    parts: [cell(23, 32, 36), cell(41, 32, 36)],
  },
  decision: {
    word: "decision",
    from: "a cell, one mark",
    frame: "cell",
    parts: [p("M22 33 L29 40 L43 25")],
  },
  capture: {
    word: "note",
    from: "#44",
    frame: "cell",
    parts: [dot(32, 32, 6.4)],
  },
  task: {
    word: "task",
    from: "a cell, one ring",
    frame: "cell",
    parts: [ring(32, 32, 7.2)],
  },
  handoff: {
    word: "handoff",
    from: "#24",
    frame: "free",
    parts: [p("M17 41 C31 41 33 23 47 23"), cell(17, 41, 24), cell(47, 23, 24)],
  },
};

/** the memory beside the code (the repo view's "thinking around it") */
export type GitThinkingKind = Extract<GitGlyphKind, "decision" | "capture" | "fold" | "handoff" | "task">;

// ── drawing ────────────────────────────────────────────────────────────────────

/** the brand cell at (cx, cy), `w` wide in the 64 box. */
function cellTransform(cx: number, cy: number, w: number): { transform: string; s: number } {
  const s = w / CELL.width;
  const h = CELL.height * s;
  return { transform: `translate(${(cx - w / 2).toFixed(2)} ${(cy - h / 2).toFixed(2)}) scale(${s.toFixed(5)})`, s };
}

export function CellShape({
  cx,
  cy,
  w,
  fill,
  stroke,
  rim,
}: {
  cx: number;
  cy: number;
  w: number;
  fill: string;
  stroke?: string;
  rim?: number;
}) {
  const { transform, s } = cellTransform(cx, cy, w);
  return (
    <path
      d={CELL.path}
      transform={transform}
      fill={fill}
      stroke={stroke}
      strokeWidth={stroke && rim ? rim / s : undefined}
      strokeLinejoin="round"
    />
  );
}

/** strokes, nodes and rings of a glyph in one ink (black/white in a mask, currentColor on the page). */
function Marks({ parts, ink, stroke, node }: { parts: Part[]; ink: string; stroke: number; node: number }) {
  return (
    <>
      {parts.map((g, i) =>
        g.t === "path" ? (
          <path key={i} d={g.d} fill="none" stroke={ink} strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" />
        ) : g.t === "dot" ? (
          <circle key={i} cx={g.x} cy={g.y} r={(g.r ?? GLYPH_NODE) * (node / GLYPH_NODE)} fill={ink} />
        ) : g.t === "ring" ? (
          <circle key={i} cx={g.x} cy={g.y} r={g.r} fill="none" stroke={ink} strokeWidth={stroke} />
        ) : null,
      )}
    </>
  );
}

const cellsOf = (parts: Part[]) => parts.filter((g): g is Extract<Part, { t: "cell" }> => g.t === "cell");
const marksOf = (parts: Part[]) => parts.filter((g) => g.t !== "cell");

/** the glyph's body inside a 64 box; `id` seeds its mask ids. */
export function GlyphBody({ def, solid, id }: { def: GitGlyphDef; solid: boolean; id: string }) {
  const cells = cellsOf(def.parts);
  const marks = marksOf(def.parts);

  if (def.frame === "cell") {
    if (!solid) {
      return (
        <>
          <CellShape cx={32} cy={32} w={FRAME_W} fill="none" stroke="currentColor" rim={GLYPH_RIM} />
          <Marks parts={marks} ink="currentColor" stroke={GLYPH_STROKE} node={GLYPH_NODE} />
        </>
      );
    }
    // the knockout: a filled cell, the glyph cut through it
    return (
      <>
        <mask id={`${id}k`} maskUnits="userSpaceOnUse" x="0" y="0" width="64" height="64">
          <rect width="64" height="64" fill="black" />
          <CellShape cx={32} cy={32} w={FRAME_W} fill="white" />
          <Marks parts={marks} ink="black" stroke={KNOCK_STROKE} node={KNOCK_NODE} />
        </mask>
        <rect width="64" height="64" fill="currentColor" mask={`url(#${id}k)`} />
        {def.escapes && (
          // the thread beyond the rim, in ink
          <>
            <mask id={`${id}o`} maskUnits="userSpaceOnUse" x="-8" y="-8" width="80" height="80">
              <rect x="-8" y="-8" width="80" height="80" fill="white" />
              <CellShape cx={32} cy={32} w={FRAME_W} fill="black" />
            </mask>
            <g mask={`url(#${id}o)`}>
              <Marks parts={marks} ink="currentColor" stroke={KNOCK_STROKE} node={KNOCK_NODE} />
            </g>
          </>
        )}
      </>
    );
  }

  // free: the glyph is cells on a thread
  if (!solid) {
    return (
      <>
        {/* the thread stops at each rim, never runs through a hollow cell */}
        <mask id={`${id}t`} maskUnits="userSpaceOnUse" x="-8" y="-8" width="80" height="80">
          <rect x="-8" y="-8" width="80" height="80" fill="white" />
          {cells.map((c, i) => (
            <CellShape key={i} cx={c.cx} cy={c.cy} w={c.w} fill="black" />
          ))}
        </mask>
        <g mask={`url(#${id}t)`}>
          <Marks parts={marks} ink="currentColor" stroke={GLYPH_STROKE} node={GLYPH_NODE} />
        </g>
        {cells.map((c, i) => (
          <CellShape key={i} cx={c.cx} cy={c.cy} w={c.w} fill="none" stroke="currentColor" rim={GLYPH_RIM} />
        ))}
      </>
    );
  }
  // solid: the cells filled, each parted from a cell it overlaps by a thin gap;
  // the thread runs on into them unbroken
  return (
    <>
      <mask id={`${id}s`} maskUnits="userSpaceOnUse" x="-8" y="-8" width="80" height="80">
        <rect x="-8" y="-8" width="80" height="80" fill="black" />
        {cells.map((c, i) => (
          <g key={i}>
            {i > 0 && <CellShape cx={c.cx} cy={c.cy} w={c.w} fill="none" stroke="black" rim={GAP * 2} />}
            <CellShape cx={c.cx} cy={c.cy} w={c.w} fill="white" />
          </g>
        ))}
        <Marks parts={marks} ink="white" stroke={GLYPH_STROKE} node={GLYPH_NODE} />
      </mask>
      <rect x="-8" y="-8" width="80" height="80" fill="currentColor" mask={`url(#${id}s)`} />
    </>
  );
}

export default function GitGlyph({
  kind,
  solid = false,
  size = 16,
  title,
  className,
}: {
  kind: GitGlyphKind;
  /** the filled / knockout form */
  solid?: boolean;
  /** px, or any CSS length (".85em" beside a word) */
  size?: number | string;
  /** a label for assistive tech; omitted, the glyph is decorative */
  title?: string;
  className?: string;
}) {
  const raw = useId();
  const id = `gg${raw.replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const def = GIT_GLYPHS[kind] ?? GIT_GLYPHS.commit;
  return (
    <svg
      className={className ? `git-glyph ${className}` : "git-glyph"}
      viewBox="0 0 64 64"
      width={size}
      height={size}
      role={title ? "img" : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title || undefined}
      focusable="false"
      style={{ flex: "0 0 auto", overflow: "visible" }}
    >
      <GlyphBody def={def} solid={solid} id={id} />
    </svg>
  );
}
